// Browser side of D1. The browser holds only the opaque session ID (never a Halo token) and sends
// it as X-Session on every /api call. A 401 { reauth: true } anywhere signs the agent out locally.

export interface Agent {
  id: number;
  name: string;
  email: string;
}

export interface SessionIdStore {
  get(): string | null;
  set(id: string): void;
  clear(): void;
}

export const SESSION_KEY = 'halo.session_id';

/** iframe localStorage, shared by every mode of the app (one storage partition, captured). */
export const localSessionStore: SessionIdStore = {
  get: () => {
    try {
      return localStorage.getItem(SESSION_KEY);
    } catch {
      return null;
    }
  },
  set: (id) => {
    try {
      localStorage.setItem(SESSION_KEY, id);
    } catch {
      // storage blocked: the session lasts as long as this instance
    }
  },
  clear: () => {
    try {
      localStorage.removeItem(SESSION_KEY);
    } catch {
      // ignore
    }
  },
};

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly body: unknown,
  ) {
    super(`API ${status}`);
    this.name = 'ApiError';
  }

  get reauth(): boolean {
    return this.status === 401;
  }
}

export interface PendingSignIn {
  state: string;
  verifier: string;
  authorizeUrl: string;
}

export type SignOutReason = 'rejected' | 'signout';

export type ClaimResult = { status: 'pending' } | { status: 'done'; agent: Agent } | { status: 'expired' };

type FetchLike = (input: string, init?: RequestInit) => Promise<Response>;

const b64url = (bytes: Uint8Array): string => btoa(String.fromCharCode(...bytes)).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');

export const randomToken = (bytes = 32): string => b64url(crypto.getRandomValues(new Uint8Array(bytes)));

export async function challengeFor(verifier: string): Promise<string> {
  return b64url(new Uint8Array(await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier))));
}

export class AuthManager {
  private readonly listeners = new Set<(reason: SignOutReason) => void>();

  constructor(
    private readonly ids: SessionIdStore = localSessionStore,
    private readonly fetchFn: FetchLike = (input, init) => fetch(input, init),
  ) {}

  get signedIn(): boolean {
    return this.ids.get() !== null;
  }

  /** Called whenever the session ends: Halo rejected it, or the agent signed out. */
  onSignedOut(listener: (reason: SignOutReason) => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  /**
   * The agent for the stored session, or null when there is none or the server rejected it.
   * Throws ApiError when the server couldn't decide (Halo or the API down): keep the session.
   */
  async checkSession(): Promise<Agent | null> {
    if (!this.signedIn) return null;
    try {
      return (await this.api<{ agent: Agent }>('/api/auth/session')).agent;
    } catch (e) {
      if (e instanceof ApiError && e.reauth) return null;
      throw e;
    }
  }

  /** Calls our API as the agent. */
  async api<T>(path: string, init: RequestInit = {}): Promise<T> {
    const headers = new Headers(init.headers);
    const id = this.ids.get();
    if (id) headers.set('X-Session', id);
    if (init.body && !headers.has('Content-Type')) headers.set('Content-Type', 'application/json');
    const res = await this.fetchFn(path, { ...init, headers });
    const body: unknown = res.status === 204 ? undefined : await res.json().catch(() => undefined);
    if (res.status === 401) this.endSession('rejected');
    if (!res.ok) throw new ApiError(res.status, body);
    return body as T;
  }

  async beginSignIn(): Promise<PendingSignIn> {
    const state = randomToken();
    const verifier = randomToken(48);
    const res = await this.post<{ authorize_url: string }>('/api/auth/begin', { state, challenge: await challengeFor(verifier) });
    return { state, verifier, authorizeUrl: res.authorize_url };
  }

  async claim(p: Pick<PendingSignIn, 'state' | 'verifier'>): Promise<ClaimResult> {
    const res = await this.fetchFn('/api/auth/claim', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ state: p.state, verifier: p.verifier }),
    });
    if (res.status === 202) return { status: 'pending' };
    if (res.status === 410) return { status: 'expired' };
    const body = (await res.json().catch(() => undefined)) as { session_id?: string; agent?: Agent } | undefined;
    if (!res.ok || !body?.session_id || !body.agent) throw new ApiError(res.status, body);
    this.ids.set(body.session_id);
    return { status: 'done', agent: body.agent };
  }

  async signOut(): Promise<void> {
    if (this.signedIn) await this.post('/api/auth/signout', {}).catch(() => undefined);
    this.endSession('signout');
  }

  private post<T>(path: string, body: unknown): Promise<T> {
    return this.api<T>(path, { method: 'POST', body: JSON.stringify(body) });
  }

  private endSession(reason: SignOutReason): void {
    const had = this.signedIn;
    this.ids.clear();
    if (had) this.listeners.forEach((l) => l(reason));
  }
}
